import { Alert, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { use } from 'react';
import { DataSourceActions } from 'src/common/components/data-states/DataSourceActions';
import { TabShowsItsOwnEmptyState } from 'src/common/components/data-states/TabShowsItsOwnEmptyState';
import { ScenarioScope } from './scope';
import { CashflowReadiness } from './types';

const FAMILY_SIESTA_PROMPT =
  "Please review the household's profile and update the recorded incomes, expenditure, savings, " +
  'pensions and property from their documents so a cashflow model has richer data. Ask me for ' +
  "anything you can't find.";

const CLIENT_SIESTA_PROMPT =
  "Please review this client's profile and update the recorded incomes, expenditure, savings, " +
  'pensions and property from their documents so a cashflow model has richer data. Ask me for ' +
  "anything you can't find.";

type Props = {
  readiness: CashflowReadiness;
  scope: ScenarioScope;
  compact?: boolean;
};

const GROUP_LABELS: Record<string, string> = {
  incomes: 'incomes',
  expenditures: 'expenditure',
  savings_or_pensions: 'savings, investments or pensions',
  bank_accounts: 'bank accounts',
};

const missingSentence = (readiness: CashflowReadiness): string => {
  const labels = readiness.missing.map(group => GROUP_LABELS[group] ?? group);
  if (labels.length === 0) return '';
  const joined =
    labels.length === 1
      ? labels[0]
      : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
  return ` Nothing is recorded yet for ${joined}.`;
};

// Shown before and during model creation when the profile is thin: the
// adviser can still proceed, but a seeded model is only as good as the
// profile, so the two fixes are one click away.
export function ModelDataCallout({ readiness, scope, compact = false }: Props) {
  // The tab above is already offering these same two buttons under its own "nothing recorded
  // yet" callout, and two callouts saying the same thing read as a bug rather than as emphasis.
  const tabIsAlreadySayingThis = use(TabShowsItsOwnEmptyState);
  if (readiness.level === 'rich' || tabIsAlreadySayingThis) {
    return null;
  }
  const members = readiness.members.length > 0 ? readiness.members : [];
  const subject = scope.kind === 'family' ? 'This household has' : 'This client has';
  const detail =
    readiness.level === 'empty'
      ? `${subject} no financial data on the profile yet.`
      : `${subject} limited financial data on the profile.${missingSentence(readiness)}`;

  return (
    <Alert
      color="yellow"
      variant="light"
      icon={<IconAlertTriangle size={18} />}
      title="Get richer model data first"
      data-testid="model-data-callout"
    >
      <Text size="sm">
        {detail} Process their documents or ask Siesta to update the client data before creating the
        model, so we can pre-populate it with pensions, investments, ISAs, income and other relevant
        information. You can still create the model now and enter the details by hand.
      </Text>
      <DataSourceActions
        subjects={
          scope.kind === 'client'
            ? [{ clientId: scope.id }]
            : members.map(member => ({
                clientId: member.clientId,
                firstName: member.name.split(' ')[0],
              }))
        }
        siestaPrompt={scope.kind === 'family' ? FAMILY_SIESTA_PROMPT : CLIENT_SIESTA_PROMPT}
        siestaLabel="Ask Siesta to update the profile"
        compact={compact}
      />
    </Alert>
  );
}
